import React, { useEffect } from "react";
import classNames from "classnames";
import { useParams, useSearchParams } from "react-router-dom";
import { InvoiceSource } from "@common/types/invoice";
import { OrganizationMemberClientAccessSource } from "@common/types/organizationMemberClientAccess";
import { NATIVE_INVOICE_DEFAULT_PAGE_SIZE } from "@common/constants/nativeInvoice";
import { useInvoices } from "@api/invoice.api";
import { usePreviewNativeInvoice } from "@api/native-invoice.api";
import { useCacheInvalidation } from "@hooks/useCacheInvalidation";
import { useMobileBlockScreen } from "@hooks/useMobileView";
import { useStateAwareNavigate } from "@hooks/useStateAwareNavigate";
import { useScope } from "@state/scopeContext.lib";
import { Button } from "@design-system/Button/Button";
import { Loader } from "@design-system/Loader/Loader";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { LeftArrowIcon } from "@icons";
import { InvoicePreviewDocument } from "./InvoicePreviewDocument";
import styles from "./InvoicePreviewPage.module.scss";

// Read-only invoice PDF viewer at /invoicing/preview/{invoiceId}.
// Desktop's only control is back to invoicing. Mobile is the document
// itself: the rest of the app is blocked on small screens, so this route
// is the exception and offers no other action.
// Only native (Kick-generated) invoices have a renderable PDF here — other
// sources (Stripe/Mercury/BILL/etc.) fall through to the same error state a
// failed render would show, since there is nothing to preview for them.
export const InvoicePreviewPage: React.FC = () => {
    const { invoiceId } = useParams<{ invoiceId: string }>();
    const [searchParams] = useSearchParams();
    const { organizationId, workspaceId, initializeScope } = useScope();
    const isMobile = useMobileBlockScreen();
    const navigate = useStateAwareNavigate();
    useCacheInvalidation();

    // Deep links (e.g. from MCP) carry the invoice's workspace so the page can
    // select it before loading; otherwise the invoice search runs against the
    // browser's active workspace and fails to find it.
    const targetWorkspaceId = searchParams.get("workspaceId");
    const isWorkspaceReady =
        !targetWorkspaceId || targetWorkspaceId === workspaceId;

    useEffect(() => {
        if (targetWorkspaceId && targetWorkspaceId !== workspaceId) {
            initializeScope({
                organizationId,
                workspaceId: targetWorkspaceId,
                source: OrganizationMemberClientAccessSource.HYDRATE,
            });
        }
    }, [initializeScope, organizationId, targetWorkspaceId, workspaceId]);

    const invoiceQuery = useInvoices(
        { filters: { ids: invoiceId ? [invoiceId] : [] }, page: 1, limit: 1 },
        { enabled: Boolean(invoiceId) && isWorkspaceReady },
    );
    const invoice = invoiceQuery.data?.data?.[0];
    const isNativeInvoice = invoice?.source === InvoiceSource.MANUAL;

    const previewQuery = usePreviewNativeInvoice({
        workspaceId: workspaceId ?? undefined,
        entityId: invoice?.entityId,
        invoiceId,
        enabled: Boolean(invoiceId) && isNativeInvoice,
    });

    const isLoading =
        !isWorkspaceReady ||
        invoiceQuery.isLoading ||
        (isNativeInvoice && previewQuery.isLoading);

    const hasError =
        !invoiceId ||
        invoiceQuery.isError ||
        (invoiceQuery.isSuccess && !invoice) ||
        (invoiceQuery.isSuccess && Boolean(invoice) && !isNativeInvoice) ||
        previewQuery.isError ||
        (previewQuery.isSuccess && !previewQuery.data);

    const pageSize =
        previewQuery.data?.renderSnapshot.pageSize ??
        NATIVE_INVOICE_DEFAULT_PAGE_SIZE;

    let body: React.ReactNode;
    if (isLoading) {
        body = (
            <div className={styles.status}>
                <Loader />
            </div>
        );
    } else if (hasError || !previewQuery.data) {
        body = (
            <div className={styles.status}>
                <EmptyState
                    header="Couldn't load this invoice"
                    body="This invoice doesn't have a PDF preview available, or it failed to render."
                />
            </div>
        );
    } else {
        body = (
            <InvoicePreviewDocument
                html={previewQuery.data.html}
                pageSize={pageSize}
                layout={isMobile ? "scroll" : "fit"}
            />
        );
    }

    return (
        <div
            className={classNames(styles.page, {
                [styles.pageScrollable]: isMobile,
            })}
        >
            {!isMobile && (
                <header className={styles.toolbar}>
                    <Button
                        variant="primary"
                        size="sm"
                        leftIcon={<LeftArrowIcon />}
                        onClick={() => navigate("/invoicing")}
                    >
                        Invoicing
                    </Button>
                </header>
            )}
            {body}
        </div>
    );
};
